import React, { useEffect, useCallback, useState, useReducer } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Flex } from 'reflexbox'
import { useParams, useHistory } from 'react-router-dom'

import Breadcrumb from 'components/Breadcrumb'
import Card from 'components/Card'
import Button from 'components/Button'
import Loader from 'components/Loader'
import { TextField, SelectField } from 'components/Form'
import { Title, Text } from 'components/Typography'
import { cpfMask } from 'helpers/masks'
import { reducer } from 'helpers/loadingReducer'

import { editBeneficiary, getBeneficiary } from 'services/beneficiaries'
import { getGroups, deleteGroupBeneficiary, attributeBeneficiary } from 'services/groups'
import { toast } from 'react-toastify'

const validationSchema = Yup.object().shape({
  name: Yup.string().min(2, 'Nome muito curto'),
  email: Yup.string().email(),
})

const Edit = () => {
  const history = useHistory()
  const { id: beneficiaryID, name: beneficiaryName } = useParams()
  const [beneficiary, setBeneficiary] = useState(null)
  const [requestStatus, dispatch] = useReducer(reducer, { isLoading: true, error: false })
  const [paginationParams, setPaginationParams] = useState({ page: 1, limit: 6 })

  const handleSubmit = useCallback(async ({ groups, ...values }) => {
    try {
      await editBeneficiary(beneficiaryID, values)

      const groupsToRemove = beneficiary.groups.filter(({ value }) => !groups?.find(group => group.value === value))
      const groupsToAdd = groups?.filter(({ value }) => !beneficiary.groups.find(group => group.value === value)) || []

      for (let group of groupsToRemove) {
        let { value: groupId } = group

        await deleteGroupBeneficiary(groupId, beneficiaryID)
      }

      for (let group of groupsToAdd) {
        let { value: groupId } = group

        await attributeBeneficiary(groupId, values)
      }

      toast.success('Dados do beneficiário atualizados com sucesso!')
    } catch (e) {
      console.log(e)
      toast.error('Não foi possível atualizar os dados do beneficiário.')
    }
  })

  let toastfyLoadOptionsErrorID = null

  const loadOptions = useCallback(
    async search => {
      try {
        if (search) {
          const { data } = await getGroups({ search })
          const formattedData = data.data.map(group => ({ label: group.name, value: group.id }))

          return {
            options: formattedData,
            hasMore: false,
          }
        } else {
          const { data } = await getGroups({ ...paginationParams })
          const formattedData = data.data.map(group => ({ label: group.name, value: group.id }))

          if (data.page !== data.total_pages) setPaginationParams({ page: data.page + 1, limit: data.limit })

          return {
            options: formattedData,
            hasMore: data.page === data.total_pages ? false : true,
          }
        }
      } catch (e) {
        console.log(e)
        if (!toast.isActive(toastfyLoadOptionsErrorID)) {
          toastfyLoadOptionsErrorID = toast.error('Não foi possível carregar a lista de grupos.')
        }
        return {
          options: [],
          hasMore: false,
        }
      }
    },
    [paginationParams],
  )

  useEffect(() => {
    const fetchBeneficiaryData = async () => {
      try {
        const { data } = await getBeneficiary(beneficiaryID)

        const beneficiary = {
          ...data,
          groups: data.groups.map(({ id, name }) => ({ value: id, label: name })),
        }

        setBeneficiary(beneficiary)
        dispatch({ type: 'success' })
      } catch (e) {
        console.log(e)
        setBeneficiary({})
        dispatch({ type: 'error' })
        toast.error('Não foi possível carregar os dados do beneficiário.')
      }
    }

    fetchBeneficiaryData()
  }, [])

  const breadcrumbPath = [
    {
      label: 'Beneficiários',
      link: '/prepaid-purchase/beneficiaries',
    },
    {
      label: beneficiaryName,
      link: `/prepaid-purchase/beneficiaries/${beneficiaryID}`,
    },
    {
      label: 'Editar beneficiário',
    },
  ]

  return (
    <Flex width={1} flexDirection='column'>
      <Breadcrumb children={breadcrumbPath} />
      <Title as='h3'>Editar beneficiário</Title>
      {requestStatus.isLoading ? (
        <Flex justifyContent='center' alignItems='center' height='200px'>
          <Loader />
        </Flex>
      ) : (
        <Card mt={24}>
          <Formik initialValues={{ ...beneficiary }} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ isSubmitting }) => (
              <Form>
                <Flex flexDirection='column' width={[1, '280px']}>
                  <TextField
                    name='document'
                    type='text'
                    label='CPF'
                    placeholder='CPF'
                    disabled
                    mb={32}
                    mask={cpfMask}
                  />
                  <TextField name='name' type='text' label='Nome' placeholder='Nome' mb={32} />
                  <TextField name='email' type='email' label='E-mail' placeholder='E-mail' mb={32} />
                  <SelectField
                    mb='24px'
                    label='Grupos'
                    placeholder='Selecione'
                    name='groups'
                    multiple
                    loadOptions={loadOptions}
                  />
                </Flex>
                <Flex width={[1, '584px']} justifyContent='space-between'>
                  <Button size='big' onClick={() => history.goBack()} type='button' outlined>
                    Voltar
                  </Button>
                  <Button size='big' type='submit' disabled={isSubmitting}>
                    Salvar
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </Card>
      )}
    </Flex>
  )
}

export default Edit
